import React, { useEffect, useRef, useState } from "react";
import { Menu, X, ChevronDown, MessageCircle, Phone, PenSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

// Réunion / FR — la homepage sert principalement le marché FR/974
const NAVBAR_WA_NUMBER = "262693482110";
const NAVBAR_WA_MSG = encodeURIComponent(
  "Bonjour EM Group, je vous contacte depuis votre site — j'aimerais échanger sur mon projet.",
);
const NAVBAR_WA_URL = `https://wa.me/${NAVBAR_WA_NUMBER}?text=${NAVBAR_WA_MSG}`;
const NAVBAR_PHONE = "+262262667970";
const NAVBAR_PHONE_DISPLAY = "+262 262 66 79 70";

function trackContactClick(channel) {
  if (typeof window !== "undefined" && window.fbq) {
    try {
      window.fbq("trackCustom", "NavbarContactClick", { channel });
      window.fbq("track", "Contact");
    } catch (_e) {
      /* noop */
    }
  }
}

function ContactDropdown({ onOpenQuote, mobile = false, onCloseMobile }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      hint: "+262 693 48 21 10",
      Icon: MessageCircle,
      color: "text-emerald-300",
      onClick: () => trackContactClick("whatsapp"),
      href: NAVBAR_WA_URL,
      target: "_blank",
      rel: "noreferrer noopener",
    },
    {
      key: "phone",
      label: "Appelez-nous",
      hint: NAVBAR_PHONE_DISPLAY,
      Icon: Phone,
      color: "text-sky-300",
      onClick: () => trackContactClick("phone"),
      href: `tel:${NAVBAR_PHONE}`,
    },
    {
      key: "quote",
      label: "Demander un devis",
      hint: "Formulaire complet · réponse 24h",
      Icon: PenSquare,
      color: "text-[#FFB4BB]",
      onClick: () => {
        trackContactClick("quote");
        setOpen(false);
        if (mobile && onCloseMobile) onCloseMobile();
        onOpenQuote();
      },
    },
  ];

  return (
    <div ref={wrapperRef} className={`relative ${mobile ? "w-full" : ""}`}>
      <Button
        data-testid={mobile ? "nav-mobile-contact-toggle" : "nav-contact-toggle"}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`rounded-full gradient-bg text-white font-medium hover:opacity-90 transition-all btn-shine border-0 inline-flex items-center gap-2 ${
          mobile ? "w-full justify-center py-3" : "px-6 py-2"
        }`}
      >
        Nous contacter
        <ChevronDown
          size={14}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </Button>

      {open && (
        <div
          role="menu"
          data-testid={mobile ? "nav-mobile-contact-menu" : "nav-contact-menu"}
          className={`${
            mobile
              ? "mt-2 w-full"
              : "absolute right-0 mt-2 w-72"
          } rounded-2xl bg-[#0A0A0F]/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 p-1.5 z-50`}
        >
          {items.map(({ key, label, hint, Icon, color, onClick, href, target, rel }) => {
            const inner = (
              <>
                <span
                  className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/5 border border-white/10 ${color}`}
                >
                  <Icon size={16} />
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-sm text-white font-medium">{label}</span>
                  <span className="block text-[11px] text-white/45 truncate">{hint}</span>
                </span>
              </>
            );
            const commonCls =
              "flex items-center gap-3 w-full text-left px-2.5 py-2.5 rounded-xl hover:bg-white/[0.05] transition";
            if (href) {
              return (
                <a
                  key={key}
                  role="menuitem"
                  data-testid={`nav-contact-${key}`}
                  href={href}
                  target={target}
                  rel={rel}
                  onClick={() => {
                    onClick();
                    setOpen(false);
                    if (mobile && onCloseMobile) onCloseMobile();
                  }}
                  className={commonCls}
                >
                  {inner}
                </a>
              );
            }
            return (
              <button
                key={key}
                type="button"
                role="menuitem"
                data-testid={`nav-contact-${key}`}
                onClick={onClick}
                className={commonCls}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Navbar({ onOpenQuote }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Accompagnement", href: "#accompagnement" },
    { label: "À propos", href: "#about" },
    { label: "Branding", href: "#branding" },
    { label: "Sites web", href: "#sites-web" },
    { label: "Visuels", href: "#tarifs" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#050507]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/brand/logo-em-group.png"
            alt="EM Group"
            className="h-10 w-10 object-contain"
            style={{ maxWidth: 40, maxHeight: 40 }}
          />
        </a>

        <nav className="hidden md:flex items-center gap-10 text-sm text-white/70">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ContactDropdown onOpenQuote={onOpenQuote} />
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[#050507]/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-6 flex flex-col gap-5 text-white/80">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <div className="mt-2">
              <ContactDropdown
                onOpenQuote={onOpenQuote}
                mobile
                onCloseMobile={() => setMobileOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
